import { Nothing, Just, orElse } from 'module/helpers/core/maybe';
import { drop, map, pipe } from 'ramda';
import type { Maybe } from 'module/helpers/core/maybe';

type Strength =
  | '10'
  | '20'
  | '30'
  | '40'
  | '50'
  | '60'
  | '70'
  | '80'
  | '90'
  | '100';
type Hue =
  | 'black'
  | 'blue'
  | 'cool_gray'
  | 'coolest_gray'
  | 'cyan'
  | 'gray'
  | 'green'
  | 'magenta'
  | 'purple'
  | 'red'
  | 'teal'
  | 'yellow'
  | 'warm_gray'
  | 'white';
type OzoneHueStrengthBasedColor = `${Hue}-${Strength}`;
type OzoneHueBasedColor = Hue;
type OzoneHueBackgroundColor = `${Hue}-background`;
type OzoneHueSurfaceColor = `${Hue}-surface`;
type OzoneSemantiColor =
  | 'warning'
  | 'success'
  | 'error'
  | 'info'
  | 'danger'
  | 'border';
type OzoneColor =
  | OzoneHueBasedColor
  | OzoneHueStrengthBasedColor
  | OzoneHueBackgroundColor
  | OzoneHueSurfaceColor
  | OzoneSemantiColor;
type OzoneColorVariable<T extends OzoneColor> = `--ozone-color-${T}`;
type Red = ['red', number];
const Red = (red: number): Red => ['red', red];

type Green = ['green', number];
const Green = (green: number): Green => ['green', green];

type Blue = ['blue', number];
const Blue = (blue: number): Blue => ['blue', blue];

type Alpha = ['alpha', number];
const Alpha = (alpha: number): Alpha => ['alpha', alpha];

export type Rgb = ['rgb', number, number, number];
export type Rgba = ['rgba', number, number, number, number];
type HexDigit =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | 'A'
  | 'B'
  | 'C'
  | 'D'
  | 'E'
  | 'F';
type HexColor = `${HexDigit}${HexDigit}`;
export type Hex = ['#', HexColor, HexColor, HexColor];
const hex_digits = [
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
];
const parse = (value: `#${string}`): Maybe<Hex> => {
  if (value.length !== 7) {
    return Nothing();
  }
  const split = value.split('#');

  if (split.length !== 2) {
    return Nothing();
  }
  const hex_digit_candidates = split[1].split('').map((x) => x.toUpperCase());

  if (hex_digit_candidates.some((x) => !hex_digits.includes(x))) {
    return Nothing();
  }

  return Just([
    '#',
    `${hex_digit_candidates[0]}${hex_digit_candidates[1]}` as HexColor,
    `${hex_digit_candidates[2]}${hex_digit_candidates[3]}` as HexColor,
    `${hex_digit_candidates[4]}${hex_digit_candidates[5]}` as HexColor,
  ]);
};
const empty: Hex = ['#', '00', '00', '00'];

export const Hex = {
  parse,
  of: (value: `#${string}`): Hex => orElse(empty)(parse(value)),
  toString: (hex: Hex) => hex.join(''),
  toRgb: (hex: Hex): Rgb =>
    pipe(
      drop(1),
      map((h) => `0x${h}`),
      map(parseInt),
      ([r, g, b]) => Rgb.of(Red(r), Green(g), Blue(b)),
    )(hex),
};

export type ColorDefinition<T extends OzoneColor = OzoneColor> = {
  variable: OzoneColorVariable<T>;
  name: T;
  hex: Hex;
};

const of = <T extends OzoneColor>(
  name: T,
  hex: `#${string}`,
): ColorDefinition<T> => ({
  name,
  variable: `--ozone-color-${name}`,
  hex: Hex.of(hex),
});

const ColorDefinition = {
  of,
};

export const colors = [
  ColorDefinition.of('cool_gray-10', '#f2f4f8'),
  ColorDefinition.of('cool_gray-20', '#dde1e6'),
  ColorDefinition.of('cool_gray-30', '#c1c7cd'),
  ColorDefinition.of('cool_gray-40', '#a2a9b0'),
];

export const border = ColorDefinition.of('border', '#e0e0e0');

export const Rgba = {
  of: (red: Red, green: Green, blue: Blue, alpha: Alpha): Rgba => [
    'rgba',
    red[1],
    green[1],
    blue[1],
    alpha[1],
  ],
  toString: ([, ...values]: Rgba): string => `rgba(${values.join(',')})`,
  toRgb: (value: Rgba): Rgb => {
    const [, red, green, blue] = value;

    return Rgb.of(Red(red), Green(green), Blue(blue));
  },
};

export const Rgb = {
  of: (red: Red, green: Green, blue: Blue): Rgb => [
    'rgb',
    red[1],
    green[1],
    blue[1],
  ],
  toString: ([, ...values]: Rgb): string => `rgba(${values.join(',')})`,
  toRgba:
    (alpha: number) =>
    (value: Rgb): Rgba => {
      const [, red, green, blue] = value;

      return Rgba.of(Red(red), Green(green), Blue(blue), Alpha(alpha));
    },
};
